import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  CalendarCheck, 
  Scale, 
  Heart, 
  MessageSquare,
  LogOut,
  Menu,
  X,
  UserCog,
  HardHat,
  ClipboardList,
  ListOrdered,
  Activity
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/roster', icon: Users, label: 'Roster' },
  { to: '/attendance', icon: CalendarCheck, label: 'Attendance' },
  { to: '/weight', icon: Scale, label: 'Weight' },
  { to: '/practice', icon: ClipboardList, label: 'Practice' },
  { to: '/depth-chart', icon: ListOrdered, label: 'Depth Chart' },
  { to: '/equipment', icon: HardHat, label: 'Equipment' },
  { to: '/injuries', icon: Heart, label: 'Injuries' },
  { to: '/rehab', icon: Activity, label: 'Rehab' },
  { to: '/alerts', icon: MessageSquare, label: 'Alerts' },
];

export function Layout({ children }) {
  const { user, logout, canApproveUsers } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'head_coach': return 'Head Coach';
      case 'trainer': return 'Athletic Trainer';
      case 'coach': return 'Coach';
      default: return role;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-[#27272A] bg-[#0A0A0A]">
        {/* Logo */}
        <div className="p-6 border-b border-[#27272A]">
          <h1 className="font-display text-2xl text-white tracking-tight">T.O.T</h1>
          <p className="text-xs text-[#71717A] mt-1 uppercase tracking-widest">
            {getRoleDisplay(user?.role)}
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'nav-link',
                  isActive && 'active'
                )
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
          
          {/* User Management - Only for head_coach and trainer */}
          {canApproveUsers && (
            <NavLink
              to="/users"
              className={({ isActive }) =>
                cn(
                  'nav-link',
                  isActive && 'active'
                )
              }
              data-testid="nav-users"
            >
              <UserCog className="w-5 h-5" />
              <span>Users</span>
            </NavLink>
          )}
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-[#27272A]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name}</p>
              <p className="text-xs text-[#71717A] truncate">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="w-full border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1A1A] hover:text-white"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-nav">
        <div className="flex items-center justify-between p-4">
          <h1 className="font-display text-xl text-white">T.O.T</h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="bg-[#0A0A0A] border-t border-[#27272A] p-4">
            <nav className="space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-4 py-3 text-[#A1A1AA]',
                      isActive && 'text-white bg-[#1A1A1A]'
                    )
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
              {canApproveUsers && (
                <NavLink
                  to="/users"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-4 py-3 text-[#A1A1AA]',
                      isActive && 'text-white bg-[#1A1A1A]'
                    )
                  }
                >
                  <UserCog className="w-5 h-5" />
                  <span>Users</span>
                </NavLink>
              )}
            </nav>
            <Button 
              variant="outline" 
              className="w-full mt-4 border-[#27272A] text-[#A1A1AA]"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:overflow-y-auto">
        <div className="lg:hidden h-16" /> {/* Spacer for mobile header */}
        <div className="p-4 lg:p-8">
          {children}
        </div>
        <div className="lg:hidden h-20" /> {/* Spacer for mobile nav */}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav lg:hidden flex justify-around">
        {navItems.slice(0, 5).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center gap-1 p-2 text-[#71717A]',
                isActive && 'text-[#FF3B30]'
              )
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="text-[10px]">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
